import {sortBy} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  addAccount,
  addContact,
  addCustomAttribute,
  addDescription,
  addLink,
  addNewAccountAccountId,
  CardAttributeUpdateAction,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {buildContactUidWithoutHandle, ContactSourcesEnum} from '../../../models/api/contactModel';
import {CustomFieldModel} from '../../../models/api/customFieldModel';
import {ResourceMap} from '../../../store/appTypes';
import {DropdownCategory} from '../../common/dropdowns/dropdownCategory';
import {DeprecatedSearchDropdown} from '../../common/dropdowns/search/deprecatedSearchDropdown';
import {TypedRowValue} from '../../common/dropdowns/source/dropdownSource';
import {SyncDropdownSource} from '../../common/dropdowns/source/syncDropdownSource';
import {ContactDetailsVersionsEnum, useContactDetailsContext} from '../contactDetailsContext';

/*
 * Constants.
 */

const dropdownHeight = 340;
const dropdownWidth = 260;

/*
 * Types.
 */

enum DropdownItemTypesEnum {
  DEFAULT_CATEGORY = 'DEFAULT_CATEGORY',
  DEFAULT_FIELD = 'DEFAULT_FIELD',
  CUSTOM_CATEGORY = 'CUSTOM_CATEGORY',
  CUSTOM_FIELD = 'CUSTOM_FIELD',
}

interface DropdownItemField {
  searchName: string;
  content: ReactNode;
  onSelectHandler: Handler;
}

type DefaultCategoryRow = TypedRowValue<DropdownItemTypesEnum.DEFAULT_CATEGORY, ReactNode>;
type DefaultFieldRow = TypedRowValue<DropdownItemTypesEnum.DEFAULT_FIELD, DropdownItemField>;
type CustomCategoryRow = TypedRowValue<DropdownItemTypesEnum.CUSTOM_CATEGORY, ReactNode>;
type CustomFieldRow = TypedRowValue<DropdownItemTypesEnum.CUSTOM_FIELD, DropdownItemField>;

type AddInformationDropdownTypedRowValue =
  | DefaultCategoryRow
  | DefaultFieldRow
  | CustomCategoryRow
  | CustomFieldRow;

interface AddInformationPopoverProps {
  card: CardModel | undefined;
  customFieldsMap: ResourceMap<CustomFieldModel>;
  onAddInformation: HandlerOf<CardAttributeUpdateAction>;
  isUserGlobalAdmin: boolean;
  onNavigateToCustomFieldsSettings: Handler;
  isDisabled?: boolean;
}

/*
 * Styles.
 */

const StyledPopoverButtonContainerDiv = styled.div`
  margin-bottom: -6px;
  display: flex;
`;

const StyledPopoverButton = styled(Button)`
  position: relative;
  margin: -4px -12px -4px 0;
`;

const StyledIcon = styled(Icon)`
  margin-left: 2px;
  margin-right: 12px;
`;

const StyledDropdownItemContent = styled(DropdownItemContent)`
  ${flexCenter}
`;

const StyledAddCustomFieldButtonWrapperDiv = styled.div`
  position: absolute;
  right: 5px;
  top: 2px;
`;

const StyledCustomFieldDropdownCategory = styled(DropdownCategory)`
  ${flexCenter}
`;

/*
 * Component.
 */

export const AddInformationPopover: FC<AddInformationPopoverProps> = (props) => {
  const {isDisabled} = props;
  const {version} = useContactDetailsContext();

  const dropdownSource = new SyncDropdownSource({
    items: getDropdownItems(props, version),
    rowFilter,
    renderRow: (item) => renderRow(item),
  });

  return (
    <PopoverCoordinator
      isDisabled={isDisabled}
      renderButton={() => (
        <StyledPopoverButtonContainerDiv>
          <StyledPopoverButton
            variant={ButtonVariantsEnum.LINK}
            interactionId="contact-details-add-information-button"
          >
            <Trans id="contact-details-add-information-button-label">+ Add</Trans>
          </StyledPopoverButton>
        </StyledPopoverButtonContainerDiv>
      )}
      renderModal={(popoverProps) => (
        <DeprecatedSearchDropdown
          {...popoverProps}
          title={<Trans id="contacts-information-add-information-dropdown-title">Add information</Trans>}
          source={dropdownSource}
          height={dropdownHeight}
          width={dropdownWidth}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

function getDropdownItems(
  props: AddInformationPopoverProps,
  version: ContactDetailsVersionsEnum,
): ReadonlyArray<AddInformationDropdownTypedRowValue> {
  const {card, customFieldsMap, isUserGlobalAdmin, onAddInformation, onNavigateToCustomFieldsSettings} =
    props;
  return [
    buildDefaultFieldCategoryItem(),
    ...buildDefaultFieldItems(card, version, onAddInformation),
    buildCustomFieldCategoryItem(isUserGlobalAdmin, onNavigateToCustomFieldsSettings),
    ...buildCustomFieldItems(card, customFieldsMap, onAddInformation),
  ];
}

function rowFilter(items: ReadonlyArray<AddInformationDropdownTypedRowValue>, query: string) {
  if (!query) {
    return items;
  }

  const queryInLowerCase = query.toLowerCase();
  const defaultCategoryItem = items.find((item) => item.type === DropdownItemTypesEnum.DEFAULT_CATEGORY);
  const filteredDefaultItems = items.filter(
    (item) =>
      item.type === DropdownItemTypesEnum.DEFAULT_FIELD &&
      item.value.searchName.toLowerCase().includes(queryInLowerCase),
  );
  const customCategoryItem = items.find((item) => item.type === DropdownItemTypesEnum.CUSTOM_CATEGORY);
  const filteredCustomItems = items.filter(
    (item) =>
      item.type === DropdownItemTypesEnum.CUSTOM_FIELD &&
      item.value.searchName.toLowerCase().includes(queryInLowerCase),
  );

  // Only show the category header if the query matches items in that category
  return [
    filteredDefaultItems.length > 0 ? defaultCategoryItem : undefined,
    ...filteredDefaultItems,
    filteredCustomItems.length > 0 ? customCategoryItem : undefined,
    ...filteredCustomItems,
  ].filter(isDefined);
}

function renderRow(item: AddInformationDropdownTypedRowValue) {
  switch (item.type) {
    case DropdownItemTypesEnum.DEFAULT_CATEGORY:
    case DropdownItemTypesEnum.CUSTOM_CATEGORY:
      return <StyledCustomFieldDropdownCategory>{item.value}</StyledCustomFieldDropdownCategory>;

    case DropdownItemTypesEnum.DEFAULT_FIELD:
    case DropdownItemTypesEnum.CUSTOM_FIELD:
      return (
        <DropdownItem onSelect={() => item.value.onSelectHandler()}>
          <StyledDropdownItemContent>{item.value.content}</StyledDropdownItemContent>
        </DropdownItem>
      );

    default:
      return null;
  }
}

function buildDefaultFieldCategoryItem(): DefaultCategoryRow {
  return {
    type: DropdownItemTypesEnum.DEFAULT_CATEGORY,
    value: <Trans id="contact-details-add-information-default-header">Default</Trans>,
  };
}

function buildDefaultFieldItems(
  card: CardModel | undefined,
  version: ContactDetailsVersionsEnum,
  onAddInformation: HandlerOf<CardAttributeUpdateAction>,
) {
  const defaultFieldItems: Array<DefaultFieldRow> = [
    buildDefaultFieldItem(
      'Email',
      'messageSmall',
      <Trans id="contact-details-add-information-email">Email</Trans>,
      () => onAddInformation(addContact(buildContactUidWithoutHandle(ContactSourcesEnum.EMAIL))),
    ),
    buildDefaultFieldItem(
      'Phone',
      'phoneSmall',
      <Trans id="contact-details-add-information-phone">Phone</Trans>,
      () => onAddInformation(addContact(buildContactUidWithoutHandle(ContactSourcesEnum.PHONE))),
    ),
    buildDefaultFieldItem(
      'Twitter',
      'twitterSmall',
      <Trans id="contact-details-add-information-twitter">Twitter</Trans>,
      () => onAddInformation(addContact(buildContactUidWithoutHandle(ContactSourcesEnum.TWITTER))),
    ),
    buildDefaultFieldItem(
      'Twitter DM',
      'twitterSmall',
      <Trans id="contact-details-add-information-twitter-dm">Twitter DM</Trans>,
      () => onAddInformation(addContact(buildContactUidWithoutHandle(ContactSourcesEnum.TWITTER_DM))),
    ),
    buildDefaultFieldItem(
      'Custom',
      'channelsSmall',
      <Trans id="contact-details-add-information-custom">Custom</Trans>,
      () => onAddInformation(addContact(buildContactUidWithoutHandle(ContactSourcesEnum.CUSTOM))),
    ),
  ];

  if (
    version === ContactDetailsVersionsEnum.VERSION_1 &&
    (!card || (card.accountId === undefined && !isTeammateNamespace(card.namespace)))
  ) {
    defaultFieldItems.unshift(
      buildDefaultFieldItem(
        'Account',
        'briefcaseOutlineSmall',
        <Trans id="contact-details-add-information-account">Account</Trans>,
        () => onAddInformation(addAccount(addNewAccountAccountId)),
      ),
    );
  }

  if (!card || card.description === undefined) {
    defaultFieldItems.push(
      buildDefaultFieldItem(
        'Description',
        'notesSmall',
        <Trans id="contact-details-add-information-description">Description</Trans>,
        () => onAddInformation(addDescription('')),
      ),
    );
  }

  // We want link to appear after description
  defaultFieldItems.push(
    buildDefaultFieldItem(
      'Link',
      'linkSmall',
      <Trans id="contact-details-add-information-link">Link</Trans>,
      () => onAddInformation(addLink('', card?.links?.length ?? 0)),
    ),
  );

  return defaultFieldItems.filter(isDefined);
}

function buildDefaultFieldItem(
  searchName: string,
  iconName: IconName,
  display: ReactNode,
  onSelectHandler: Handler,
): DefaultFieldRow {
  return {
    type: DropdownItemTypesEnum.DEFAULT_FIELD,
    value: {
      searchName,
      content: (
        <>
          <UserThemeConsumer>
            {(theme) => <StyledIcon name={iconName} size={16} color={theme.greys.shade70} />}
          </UserThemeConsumer>
          {display}
        </>
      ),
      onSelectHandler,
    },
  };
}

function buildCustomFieldCategoryItem(
  isUserGlobalAdmin: boolean,
  onNavigateToCustomFieldsSettings: Handler,
): CustomCategoryRow {
  return {
    type: DropdownItemTypesEnum.CUSTOM_CATEGORY,
    value: (
      <>
        <Trans id="contact-details-add-information-custom-header">Custom</Trans>
        {isUserGlobalAdmin && (
          <StyledAddCustomFieldButtonWrapperDiv>
            <Button
              variant={ButtonVariantsEnum.LINK}
              size={VisualSizesEnum.SMALL}
              onClick={() => onNavigateToCustomFieldsSettings()}
              isRounded
            >
              <Trans id="contact-details-custom-add-button-label">Add</Trans>
            </Button>
          </StyledAddCustomFieldButtonWrapperDiv>
        )}
      </>
    ),
  };
}

function buildCustomFieldItems(
  card: CardModel | undefined,
  customFieldsMap: ResourceMap<CustomFieldModel>,
  onAddInformation: HandlerOf<CardAttributeUpdateAction>,
) {
  const customFields = Object.values(customFieldsMap).filter(isDefined);
  const alreadyUsedCustomFieldIds = new Set(card?.customFieldAttributes?.map((attribute) => attribute.id));
  const unusedCustomFields = customFields.filter(
    (customField) => !alreadyUsedCustomFieldIds.has(customField.id),
  );
  const customFieldItems: Array<CustomFieldRow> = unusedCustomFields.map((field) => ({
    type: DropdownItemTypesEnum.CUSTOM_FIELD,
    value: {
      searchName: field.name,
      content: field.name,
      onSelectHandler: () => onAddInformation(addCustomAttribute('', field)),
    },
  }));
  return sortBy(customFieldItems, (item) => item.value.searchName.toLowerCase());
}
